import React, { useEffect } from 'react';
import { LogIn, LogOut } from 'lucide-react';
import { Button } from '../common/Button';
import { useAuth } from '../../hooks/useAuth';

interface Organization {
  id: number;
  name: string;
  display_name?: string;
  square_environment: string;
}

interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  picture?: string;
  is_admin: boolean;
  can_access_square: boolean;
  can_access_ai: boolean;
  organization?: Organization;
  permissions?: {
    can_access_square?: boolean;
    can_access_ai?: boolean;
    can_manage_users?: boolean;
    can_manage_tokens?: boolean;
    role?: string;
  };
}

interface AuthServiceProps {
  onAuthChange?: (user: AuthUser | null) => void;
  variant?: 'header' | 'card' | 'inline' | 'button-only';
  showUserInfo?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AuthService: React.FC<AuthServiceProps> = ({ 
  onAuthChange, 
  variant = 'header',
  showUserInfo = true,
  size = 'md',
  className = ''
}) => {
  const { user, isLoading, isAuthEnabled, signIn, signOut } = useAuth();

  // Notify parent component of auth changes
  useEffect(() => {
    onAuthChange?.(user);
  }, [user, onAuthChange]);

  // If auth is disabled, don't render anything
  if (!isAuthEnabled) {
    return null;
  }

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
        <span className="text-sm text-gray-600">Checking authentication...</span>
      </div>
    );
  }

  // Render based on variant
  if (user) {
    switch (variant) {
      case 'header':
        return (
          <div className={`flex items-center space-x-4 ${className}`}>
            {showUserInfo && (
              <div className="flex items-center space-x-3">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.full_name}
                    className="w-8 h-8 rounded-full border-2 border-white/20"
                  />
                )}
                <div className="text-white">
                  <p className="text-sm font-medium">{user.full_name}</p>
                  <p className="text-xs text-white/80">{user.email}</p>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              icon={LogOut}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              Sign Out
            </Button>
          </div>
        );

      case 'card':
        return (
          <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
            <div className="flex items-center space-x-3 mb-3">
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.full_name}
                  className="w-10 h-10 rounded-full border-2 border-green-300"
                />
              )}
              <div className="flex-1">
                <p className="text-green-800 font-medium">{user.full_name}</p>
                <p className="text-green-600 text-sm">{user.email}</p>
                {user.is_admin && (
                  <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded mt-1 inline-block">
                    Admin
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={signOut}
              icon={LogOut}
              fullWidth
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              Sign Out
            </Button>
          </div>
        );

      case 'inline':
        return (
          <div className={`flex items-center space-x-3 ${className}`}>
            {user.picture && (
              <img
                src={user.picture}
                alt={user.full_name}
                className="w-6 h-6 rounded-full border border-gray-300"
              />
            )}
            <span className="text-sm text-gray-700">{user.full_name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              icon={LogOut}
              className="text-gray-500 hover:text-gray-700"
            >
              Sign Out
            </Button>
          </div>
        );

      case 'button-only':
        return (
          <Button
            variant="ghost"
            size={size}
            onClick={signOut}
            icon={LogOut}
            className={`text-gray-600 hover:text-gray-800 ${className}`}
          >
            Sign Out
          </Button>
        );

      default:
        return null;
    }
  }

  // Not signed in - render sign in button based on variant
  switch (variant) {
    case 'header':
      return (
        <div className={`flex items-center space-x-4 ${className}`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={signIn}
            loading={isLoading}
            icon={LogIn}
            className="text-white/80 hover:text-white hover:bg-white/10 border border-white/20"
          >
            {isLoading ? 'Redirecting...' : 'Sign in'}
          </Button>
        </div>
      );

    case 'card':
      return (
        <div className={`text-center ${className}`}>
          <Button
            variant="primary"
            size={size}
            onClick={signIn}
            loading={isLoading}
            icon={LogIn}
            fullWidth
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoading ? 'Redirecting...' : 'Sign in'}
          </Button>
        </div>
      );

    case 'inline':
      return (
        <Button
          variant="outline"
          size={size}
          onClick={signIn}
          loading={isLoading}
          icon={LogIn}
          className={`border-gray-300 text-gray-700 hover:bg-gray-50 ${className}`}
        >
          {isLoading ? 'Redirecting...' : 'Sign in'}
        </Button>
      );

    case 'button-only':
      return (
        <Button
          variant="primary"
          size={size}
          onClick={signIn}
          loading={isLoading}
          icon={LogIn}
          className={className}
        >
          {isLoading ? 'Redirecting...' : 'Sign in'}
        </Button>
      );

    default:
      return null;
  }
};